import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PUBLIC_CHANNELS, channelMatchesTab, type RealtimeEvent } from '@sac/contracts'
import { api } from '../lib/api'
import { qk } from '../lib/query'
import { useRealtimeStream } from '../lib/events'
import { useAuth } from '../state/AuthContext'
import { useUi } from '../state/UiContext'
import { Rail } from '../components/Rail'
import { TopBar } from '../components/TopBar'
import { ChannelTabs } from '../components/ChannelTabs'
import { Inbox } from '../components/inbox/Inbox'
import { ConversationPane } from '../components/conv/ConversationPane'
import { ContextPanel } from '../components/ctx/ContextPanel'
import { PlaceholderSection } from '../components/PlaceholderSection'
import { Clientes } from './Clientes'
import { Pedidos } from './Pedidos'
import { Relatorios } from './Relatorios'
import { Registro } from './Registro'
import { Cordialidade } from './Cordialidade'
import { Tickets } from './Tickets'
import { Config } from './Config'
import { IaSection } from '../components/IaSection'
import { Robos } from './Robos'
import { Icon } from '../components/icons'

export function AgentApp() {
  const [section, setSection] = useState('conversas')
  const { state, dispatch } = useUi()
  const { user } = useAuth()
  const qc = useQueryClient()

  /**
   * Jump from another screen straight into a thread.
   *
   * Switches section as well as selecting: selecting alone would change what the Conversas view
   * shows while leaving the person looking at Pedidos, which reads as the click having failed.
   * The status filter is cleared too — the target thread is frequently closed, and a filtered
   * list would silently drop it.
   */
  const openConversation = (id: string) => {
    dispatch({ type: 'setStatus', status: 'all' })
    dispatch({ type: 'selectConversation', id })
    setSection('conversas')
  }

  const listParams = useMemo(
    () => ({
      status: state.status === 'all' ? 'all' : state.status === 'unread' ? 'all' : state.status,
      storeIds: state.storeIds.size ? [...state.storeIds].join(',') : undefined,
      q: state.search || undefined,
      assignedAgentId: state.advAgent || undefined,
      priority: state.advPriority || undefined,
    }),
    [state.status, state.storeIds, state.search, state.advAgent, state.advPriority],
  )

  const { data: allItems = [] } = useQuery({
    queryKey: qk.conversations(listParams),
    queryFn: () => api.conversations(listParams),
    enabled: section === 'conversas',
  })
  const { data: stores = [] } = useQuery({ queryKey: qk.stores(), queryFn: api.stores })
  const { data: channelAccounts = [] } = useQuery({ queryKey: qk.channelAccounts(), queryFn: api.channelAccounts })
  const { data: agents = [] } = useQuery({ queryKey: qk.agents(), queryFn: api.agents })
  const { data: quickReplies = [] } = useQuery({ queryKey: qk.quickReplies(), queryFn: api.quickReplies })
  const { data: products = [] } = useQuery({ queryKey: qk.products(), queryFn: () => api.products() })
  const { data: linkReqs = [] } = useQuery({
    queryKey: ['link-requests'],
    queryFn: api.linkRequests,
    enabled: user?.role === 'admin',
    refetchInterval: 60_000,
  })

  const channelFiltered = useMemo(() => {
    let items = allItems.filter((i) => channelMatchesTab(state.channel, i.channelType))
    if (state.advSla) items = items.filter((i) => i.slaBreached)
    if (state.status === 'unread') items = items.filter((i) => i.unreadCount > 0)

    // Open marketplace questions ride at the top, ahead of everything else.
    //
    // They are public and pre-sale: the asker has not bought yet, anyone browsing the listing
    // reads the answer, and the marketplace scores how fast it arrives. Sorted purely by
    // recency they sank underneath hundreds of resolved threads — there were three open
    // questions and no way to notice them. Resolved ones stay where they are; pinning 604
    // answered threads to the top would just be a different way of hiding the three.
    const pinned = (i: (typeof items)[number]) =>
      PUBLIC_CHANNELS.includes(i.channelType) && i.status !== 'resolved'
    return [...items].sort((a, b) => {
      const d = Number(pinned(b)) - Number(pinned(a))
      return d !== 0 ? d : new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    })
  }, [allItems, state.channel, state.advSla, state.status])

  // Realtime: keep this dumb — invalidate the affected query, let react-query refetch. No
  // hand-rolled cache surgery; the payloads are small and this app's scale doesn't need it.
  useRealtimeStream(section === 'conversas' ? '/api/events' : null, (event: RealtimeEvent) => {
    qc.invalidateQueries({ queryKey: ['conversations'] })
    if (event.conversationId) {
      qc.invalidateQueries({ queryKey: qk.conversation(event.conversationId) })
      // A new customer message means a new suggestion is being generated; re-read so the card
      // swaps itself in when it lands, without the agent reloading.
      qc.invalidateQueries({ queryKey: qk.storedSuggestion(event.conversationId) })
    }
    // Tickets are keyed by customer, not conversation, and the event only carries the latter —
    // so invalidate the whole ticket tree rather than trying to guess which customer moved.
    if (event.type.startsWith('ticket.')) qc.invalidateQueries({ queryKey: ['tickets'] })
  })

  const currentId = state.currentConversationId ?? channelFiltered[0]?.id ?? null

  return (
    <>
      <Rail section={section} onSection={setSection} isAdmin={user?.role === 'admin'} pendingLinks={linkReqs.length} />
      <div className="main">
        <TopBar items={allItems} onOpenSection={setSection}>
          <button className="icon-btn inbox-toggle-btn" onClick={() => dispatch({ type: 'toggleInbox' })}>
            <Icon name="chat" />
          </button>
          {section === 'conversas' ? (
            <ChannelTabs
              items={allItems}
              active={state.channel}
              onSelect={(c) => dispatch({ type: 'setChannel', channel: c })}
              channelAccounts={channelAccounts}
            />
          ) : (
            <div style={{ flex: 1 }} />
          )}
        </TopBar>

        {section === 'conversas' ? (
          <div className="workspace">
            <Inbox
              items={channelFiltered}
              allItems={allItems}
              stores={stores}
              agents={agents}
              onSelect={(id) => dispatch({ type: 'selectConversation', id })}
            />
            {currentId ? (
              <>
                <ConversationPane conversationId={currentId} agents={agents} quickReplies={quickReplies} products={products} />
                <ContextPanel conversationId={currentId} agents={agents} />
              </>
            ) : (
              <section className="conv">
                <div className="ph">
                  <div className="ph-in">
                    <h2>Nenhuma conversa selecionada</h2>
                    <p>Escolha uma conversa na lista à esquerda.</p>
                  </div>
                </div>
              </section>
            )}
          </div>
        ) : section === 'ia' ? (
          <IaSection />
        ) : section === 'robos' ? (
          <Robos />
        ) : (
          section === 'clientes' ? (
            <Clientes />
          ) : section === 'pedidos' ? (
            <Pedidos onOpenConversation={openConversation} />
          ) : section === 'relatorios' ? (
            <Relatorios />
          ) : section === 'registro' ? (
            <Registro onOpenConversation={openConversation} />
          ) : section === 'cordialidade' && user?.role === 'admin' ? (
            <Cordialidade onOpenConversation={openConversation} />
          ) : section === 'tickets' ? (
            <Tickets />
          ) : section === 'config' ? (
            <Config />
          ) : (
            <PlaceholderSection section={section} />
          )
        )}
      </div>
    </>
  )
}
